/**
 * ColdMainsPerformance — Animated SVG showing how cold mains pressure
 * affects combi and unvented system viability.
 */

export function ColdMainsPerformance() {
  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox="0 0 520 280" width="100%" role="img" aria-label="Cold mains performance impact on system options">
        <title>How cold mains flow and pressure determine combi and unvented viability</title>
        <defs>
          <style>{`
            .cmp-pipe { stroke: #64748b; stroke-width: 2.5; fill: none; }
            .cmp-flow-strong { stroke: #3b82f6; stroke-width: 3; fill: none; opacity: 0.7; stroke-dasharray: 8,4; animation: cmp-dash 0.8s linear infinite; }
            .cmp-flow-weak { stroke: #3b82f6; stroke-width: 1.5; fill: none; opacity: 0.35; stroke-dasharray: 3,6; animation: cmp-dash 2s linear infinite; }
            .cmp-label { font-family: system-ui, sans-serif; font-size: 11px; fill: #475569; text-anchor: middle; }
            .cmp-heading { font-family: system-ui, sans-serif; font-size: 13px; fill: #1e293b; font-weight: 600; text-anchor: middle; }
            .cmp-component { fill: #f1f5f9; stroke: #475569; stroke-width: 1.5; }
            .cmp-ok { fill: #dcfce7; stroke: #16a34a; stroke-width: 1.5; }
            .cmp-warn { fill: #fef3c7; stroke: #f59e0b; stroke-width: 1.5; }
            .cmp-divider { stroke: #cbd5e1; stroke-width: 1; stroke-dasharray: 4,4; }
            @keyframes cmp-dash { to { stroke-dashoffset: -12; } }
          `}</style>
        </defs>

        {/* Divider */}
        <line x1="260" y1="30" x2="260" y2="270" className="cmp-divider" />

        {/* === STRONG MAINS (left) === */}
        <text x="130" y="20" className="cmp-heading">Strong mains (&gt;2 bar, &gt;12 L/min)</text>

        {/* Mains supply */}
        <rect x="30" y="50" width="50" height="30" rx="4" className="cmp-ok" />
        <text x="55" y="70" className="cmp-label" style={{ fontSize: '10px' }}>Mains</text>

        {/* Strong flow pipe */}
        <line x1="80" y1="65" x2="130" y2="65" className="cmp-pipe" />
        <line x1="80" y1="65" x2="130" y2="65" className="cmp-flow-strong" />

        {/* Combi boiler */}
        <rect x="90" y="95" width="80" height="35" rx="6" className="cmp-ok" />
        <text x="130" y="117" className="cmp-label">Combi</text>
        <line x1="130" y1="80" x2="130" y2="95" className="cmp-pipe" />

        {/* Tap output - strong */}
        <line x1="130" y1="130" x2="130" y2="155" className="cmp-pipe" />
        <line x1="130" y1="130" x2="130" y2="155" className="cmp-flow-strong" />
        <rect x="110" y="155" width="40" height="20" rx="3" className="cmp-ok" />
        <text x="130" y="169" className="cmp-label" style={{ fontSize: '9px' }}>Good flow</text>

        {/* Unvented cylinder */}
        <rect x="90" y="190" width="80" height="35" rx="6" className="cmp-ok" />
        <text x="130" y="212" className="cmp-label">Unvented cyl</text>
        <line x1="55" y1="80" x2="55" y2="207" className="cmp-pipe" />
        <line x1="55" y1="207" x2="90" y2="207" className="cmp-pipe" />
        <line x1="55" y1="80" x2="55" y2="207" className="cmp-flow-strong" />

        {/* Tick marks */}
        <text x="130" y="245" className="cmp-label" style={{ fill: '#16a34a', fontSize: '10px' }}>
          Both options viable
        </text>

        {/* === WEAK MAINS (right) === */}
        <text x="390" y="20" className="cmp-heading">Weak mains (&lt;1.5 bar, &lt;10 L/min)</text>

        {/* Mains supply */}
        <rect x="290" y="50" width="50" height="30" rx="4" className="cmp-warn" />
        <text x="315" y="70" className="cmp-label" style={{ fontSize: '10px' }}>Mains</text>

        {/* Weak flow pipe */}
        <line x1="340" y1="65" x2="390" y2="65" className="cmp-pipe" />
        <line x1="340" y1="65" x2="390" y2="65" className="cmp-flow-weak" />

        {/* Combi boiler */}
        <rect x="350" y="95" width="80" height="35" rx="6" className="cmp-warn" />
        <text x="390" y="117" className="cmp-label">Combi</text>
        <line x1="390" y1="80" x2="390" y2="95" className="cmp-pipe" />

        {/* Tap output - weak */}
        <line x1="390" y1="130" x2="390" y2="155" className="cmp-pipe" />
        <line x1="390" y1="130" x2="390" y2="155" className="cmp-flow-weak" />
        <rect x="370" y="155" width="40" height="20" rx="3" style={{ fill: '#fee2e2', stroke: '#dc2626', strokeWidth: 1.5 }} />
        <text x="390" y="169" className="cmp-label" style={{ fontSize: '9px' }}>Trickle</text>

        {/* Crossed out unvented */}
        <rect x="350" y="190" width="80" height="35" rx="6" style={{ fill: '#fee2e2', stroke: '#dc2626', strokeWidth: 1.5 }} />
        <text x="390" y="212" className="cmp-label">Unvented cyl</text>
        <line x1="350" y1="190" x2="430" y2="225" style={{ stroke: '#dc2626', strokeWidth: 2 }} />
        <line x1="430" y1="190" x2="350" y2="225" style={{ stroke: '#dc2626', strokeWidth: 2 }} />

        <text x="390" y="245" className="cmp-label" style={{ fill: '#dc2626', fontSize: '10px' }}>
          Vented cylinder recommended
        </text>

        {/* Bottom note */}
        <text x="260" y="270" className="cmp-label" style={{ fontSize: '10px', fill: '#94a3b8' }}>
          Always measure flow rate and static pressure before recommending
        </text>
      </svg>
    </figure>
  );
}
